import { useCallback, useState } from "react";
import api from "@/lib/api";

/** Direct-to-Cloudinary upload using a signature from the backend.
 *
 * Why the cache-buster: GET /api/cloudinary/signature returns a signed timestamp.
 * If a browser/proxy serves a stale cached response, Cloudinary rejects the upload as
 * "Stale request" with HTTP 400 — which gets misread as "Cloudinary not configured."
 * The backend also sets Cache-Control: no-store now, but belt-and-braces.
 */
export function useUpload() {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const upload = useCallback(async (file, { folder = "mostash/general" } = {}) => {
    if (!file) throw uploadErr("NO_FILE", "No file provided.");
    const resourceType = file.type.startsWith("video/") ? "video" : "image";

    setUploading(true);
    setProgress(0);
    try {
      // 1) get a fresh signature (cache-busted)
      let sig;
      try {
        const { data } = await api.get("/cloudinary/signature", {
          params: { folder, resource_type: resourceType, _t: Date.now() },
        });
        sig = data;
      } catch (e) {
        const detail = e?.response?.data?.detail;
        const status = e?.response?.status;
        if (status === 401) {
          throw uploadErr("AUTH", "Session expired. Please log in again.");
        }
        if (typeof detail === "string" && detail.toLowerCase().includes("not configured")) {
          throw uploadErr("NOT_CONFIGURED", detail);
        }
        throw uploadErr("SIGNATURE_FAILED", detail || e?.message || "Could not get an upload signature.");
      }

      // 2) Client-side guard before we hit Cloudinary
      const { max_bytes, allowed_mime, max_mb } = sig.constraints || {};
      if (max_bytes && file.size > max_bytes) {
        throw uploadErr(
          "FILE_TOO_LARGE",
          `${resourceType === "video" ? "Video" : "Image"} is ${(file.size / (1024 * 1024)).toFixed(1)} MB — max allowed is ${max_mb} MB.`
        );
      }
      if (Array.isArray(allowed_mime) && allowed_mime.length > 0 && !allowed_mime.includes(file.type)) {
        throw uploadErr(
          "FILE_BAD_TYPE",
          `File type "${file.type || "unknown"}" isn't accepted. Allowed: ${allowed_mime.join(", ")}.`
        );
      }

      // 3) Upload directly to Cloudinary with XHR for progress
      const form = new FormData();
      form.append("file", file);
      form.append("api_key", sig.api_key);
      form.append("timestamp", sig.timestamp);
      form.append("signature", sig.signature);
      form.append("folder", sig.folder);

      let result;
      try {
        result = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("POST", sig.upload_url);
          xhr.upload.onprogress = (e) => {
            if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
          };
          xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              try { resolve(JSON.parse(xhr.responseText)); }
              catch { reject(uploadErr("CLOUDINARY_BAD_RESPONSE", "Cloudinary returned an unparseable response.")); }
            } else {
              // Surface the real Cloudinary error so the user knows whether it's a quota / size / format issue
              let cloudinaryMsg = xhr.responseText;
              try {
                const parsed = JSON.parse(xhr.responseText);
                cloudinaryMsg = parsed?.error?.message || cloudinaryMsg;
              } catch { /* ignore */ }
              reject(uploadErr(
                "CLOUDINARY_REJECTED",
                `Cloudinary rejected the upload (HTTP ${xhr.status}): ${cloudinaryMsg}`,
                { status: xhr.status, cloudinaryMsg }
              ));
            }
          };
          xhr.onerror = () => reject(uploadErr("NETWORK", "Network error while uploading to Cloudinary."));
          xhr.send(form);
        });
      } catch (e) {
        // Best-effort feedback so the failure shows up in backend logs (admin can grep for it)
        try {
          await api.post("/cloudinary/upload-feedback", {
            folder: sig.folder,
            resource_type: resourceType,
            status_code: e.meta?.status,
            cloudinary_error: e.meta?.cloudinaryMsg || e.message,
            file_bytes: file.size,
            file_name: file.name,
            file_mime: file.type,
          });
        } catch (_silent) { /* ignore */ }
        throw e;
      }

      // 4) Record in media library (best-effort)
      try {
        await api.post("/media", {
          public_id: result.public_id,
          secure_url: result.secure_url,
          resource_type: result.resource_type,
          format: result.format,
          bytes: result.bytes,
          width: result.width,
          height: result.height,
          duration: result.duration,
          folder: sig.folder,
          original_filename: result.original_filename || file.name,
        });
      } catch (_e) {
        /* media record is non-essential */
      }

      return {
        url: result.secure_url,
        public_id: result.public_id,
        resource_type: result.resource_type,
        format: result.format,
        bytes: result.bytes,
        width: result.width,
        height: result.height,
        duration: result.duration,
      };
    } finally {
      setUploading(false);
      setProgress(0);
    }
  }, []);

  return { upload, uploading, progress };
}

function uploadErr(code, message, meta) {
  const e = new Error(message);
  e.code = code;
  if (meta) e.meta = meta;
  return e;
}

export default useUpload;
